import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import { FormContainer } from "./styles";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Checkbox, Form } from "antd";
import { INMATE_DETAILS_MODE } from ".";
import CommonDatePicker from "@/common/elements/DatePicker";
import { labels } from "@/util/label";
import dayjs from "dayjs";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import { StyledGridCol, StyledGridRow } from "@/common/styles";
import {
  debounce,
  getOptionsFromRefList,
  isNonEmptyVariable,
} from "@/util/common_function";
import FileUpload from "@/common/elements/InputElements/Upload";
import BusinessInformation from "../AddInmateMovement/BusinessInformation";
import { useDispatch, useSelector } from "react-redux";
import { setBookings } from "@/slices/bookingsSlice";
import Button from "@/common/elements/Button";
import TransportDocumentModal from "./TransportDocumentModal";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";

const HouseTrackInmate = forwardRef(
  ({ mode = INMATE_DETAILS_MODE.EDIT, houseFormRef }, ref) => {
    const dispatch = useDispatch();
    const { bookings, bookingsFieldsEnabled } = useSelector(
      (state) => state?.bookings,
    );
    const [form] = Form.useForm();
    const [inmateTrack, setInmateTrack] = useState(null);
    const [isTransportModal, setIsTransportModal] = useState(false);

    const inmateLocation = Form.useWatch("inmateLocation", form);
    const inJailType = Form.useWatch("inJailType", form);
    const movementStatus = Form.useWatch("movementStatus", form);
    const enterBusiness = Form.useWatch("enterBusiness", form) || false;
    const loanType = Form.useWatch("loanType", form);
    const isFirstTime = useRef(true);
    const [blockOptions, setBlockOptions] = useState();
    const [cellOptions, setCellOptions] = useState();
    const [bedOptions, setBedOptions] = useState();

    useEffect(() => {
      if (bookings !== null) {
        const it = bookings?.inmateTracking?.[0];
        setInmateTrack(() => ({
          ...it,
          expectedBackDateTime: isNonEmptyVariable(it?.expectedBackDate)
            ? dayjs(
                `${it?.expectedBackDate || ""} ${it?.expectedBackTime || ""}`,
              )
            : null,
          scheduleDateTime: isNonEmptyVariable(it?.scheduledDate)
            ? dayjs(`${it?.scheduledDate || ""} ${it?.scheduledTime || ""}`)
            : null,
          actualDateTime: isNonEmptyVariable(it?.actualDate)
            ? dayjs(`${it?.actualDate || ""} ${it?.actualTime || ""}`)
            : dayjs(),
          businesses: it?.enterBusiness ? it?.businesses : null,
        }));
      } else {
        setInmateTrack(null);
      }
    }, [bookings]);

    useEffect(() => {
      if (isFirstTime.current && inmateTrack) {
        if (isNonEmptyVariable(inmateTrack)) {
          form.setFieldsValue(inmateTrack);
        } else {
          form.resetFields();
        }
        isFirstTime.current = false;
      }
    }, [inmateTrack]);

    // Expose form methods to parent
    useImperativeHandle(houseFormRef, () => ({
      submitForm: () => form.submit(),
      resetForm: () => form.resetFields(),
      getFormValues: () => form.getFieldsValue(),
      getFieldsError: () => form.getFieldsError(),
      isFieldsValidating: () => form.isFieldsValidating(),
      validateFields: () => form.validateFields(),
    }));

    const updateForm = (data) => {
      if (!data?.enterBusiness) {
        data = {
          ...data,
          businesses: null,
        };
      }

      dispatch(
        setBookings({
          inmateTracking: [
            {
              ...bookings?.inmateTracking?.[0],
              ...data,
            },
          ],
        }),
      );
    };

    const dataModify = debounce(() => {
      const values = form.getFieldsValue();

      const data = {
        ...values,
        expectedBackDate: isNonEmptyVariable(values?.expectedBackDateTime)
          ? dayjs(values?.expectedBackDateTime)?.format("MM/DD/YYYY")
          : null,
        expectedBackTime: isNonEmptyVariable(values?.expectedBackDateTime)
          ? dayjs(values?.expectedBackDateTime)?.format("HH:mm")
          : null,
        scheduledDate: isNonEmptyVariable(values?.scheduleDateTime)
          ? dayjs(values?.scheduleDateTime)?.format("MM/DD/YYYY")
          : null,
        scheduledTime: isNonEmptyVariable(values?.scheduleDateTime)
          ? dayjs(values?.scheduleDateTime)?.format("HH:mm")
          : null,
        actualDate: isNonEmptyVariable(values?.actualDateTime)
          ? dayjs(values?.actualDateTime)?.format("MM/DD/YYYY")
          : null,
        actualTime: isNonEmptyVariable(values?.actualDateTime)
          ? dayjs(values?.actualDateTime)?.format("HH:mm")
          : null,
      };

      delete data?.expectedBackDateTime;
      delete data?.scheduleDateTime;
      delete data?.actualDateTime;

      updateForm(data);
    }, 800);

    const handleTransportDocument = () => {
      setIsTransportModal(!isTransportModal);
    };

    const handlecellBedListOnChange = (value, field) => {
      if (!isNonEmptyVariable(value)) return;
      let url =
        field === "blockList"
          ? value === "Holding"
            ? `${API_ENDPOINTS.cellBedList}/holdingCellList`
            : `${API_ENDPOINTS.cellBedList}/blockList`
          : `${API_ENDPOINTS.cellBedList}/${field}/${value}`;

      api.get(url).then(({ data }) => {
        if (field === "blockList") {
          const formattedData = formatOptions(data, field);
          setBlockOptions(formattedData);
        } else if (field === "cellList") {
          setCellOptions(formatDropDownOptions(data));
        } else {
          setBedOptions(formatDropDownOptions(data));
        }
      });
    };

    const formatOptions = (items = [], field) => {
      let filteredItems = items.filter((rli) => rli?.enabled) || [];
      filteredItems.unshift({
        refListItemGlobalID: "",
        defaultString: "",
        longString: "",
        enabled: true,
      });
      if (field === "blockList") {
        return filteredItems
          .filter((aa) => aa?.defaultString)
          .map((aa) => ({
            value: aa.defaultString,
            enabled: aa?.enabled,
            label: aa?.longString || aa.defaultString,
          }))
          .sort((a, b) => a.value.localeCompare(b.value));
      }
      return formatDropDownOptions(filteredItems);
    };

    return (
      <>
        <FormContainer>
          <Form
            form={form}
            initialValues={inmateTrack}
            disabled={!bookingsFieldsEnabled}
            onFieldsChange={() => {
              dataModify();
            }}
          >
            <StyledGridRow gutter={[16]}>
              <StyledGridCol span={6}>
                <FormItem
                  name="inmateLocation"
                  label={labels.moveTo}
                  rules={[
                    {
                      required: true,
                      message: "Move To is required",
                    },
                  ]}
                >
                  <Select
                    showSearch={false}
                    variant="outlined"
                    options={getOptionsFromRefList("Inmate_Location")}
                    disabled={!bookingsFieldsEnabled}
                  />
                </FormItem>
              </StyledGridCol>

              <StyledGridCol span={6}>
                <FormItem
                  name="movementStatus"
                  label={labels.status}
                  rules={[{ required: true, message: "Status is required" }]}
                >
                  <Select
                    onSelect={(value) => {
                      if (value === "Cancelled" || value === "Pending") {
                        form.setFieldValue("actualDateTime", null);
                        form.setFieldValue("scheduleDateTime", null);
                      } else if (value === "Complete") {
                        form.setFieldValue("scheduleDateTime", null);
                        form.setFieldValue("actualDateTime", dayjs());
                      }
                    }}
                    showSearch={false}
                    variant="outlined"
                    options={getOptionsFromRefList("Movement_Status")}
                    disabled={!bookingsFieldsEnabled}
                    required
                  />
                </FormItem>
              </StyledGridCol>

              <StyledGridCol span={6}>
                {movementStatus === "Pending" && (
                  <FormItem
                    name="scheduleDateTime"
                    label={labels.schduleDateTime}
                  >
                    <CommonDatePicker
                      placeholder="MM/DD/YYYY HH:mm"
                      format="MM/DD/YYYY HH:mm"
                      showTime={true}
                      style={{
                        border: "2px solid rgba(82, 140, 181, 0.27)",
                      }}
                    />
                  </FormItem>
                )}
              </StyledGridCol>

              <StyledGridCol span={6}>
                <FormItem name="actualDateTime" label={labels.actualDateTime}>
                  <CommonDatePicker
                    disabled={!bookingsFieldsEnabled}
                    placeholder="MM/DD/YYYY HH:mm"
                    format="MM/DD/YYYY HH:mm"
                    showTime={true}
                    disableFutureDates={true}
                    style={{
                      border: "2px solid rgba(82, 140, 181, 0.27)",
                    }}
                  />
                </FormItem>
              </StyledGridCol>
            </StyledGridRow>

            {inmateLocation === "In Jail" && (
              <StyledGridRow gutter={[16]}>
                <StyledGridCol span={6}>
                  <FormItem
                    label={labels.jailType}
                    name="inJailType"
                    rules={[
                      { required: true, message: "Jail Type is required" },
                    ]}
                  >
                    <Select
                      placeholder="Jail Type"
                      showSearch={false}
                      variant="outlined"
                      options={getOptionsFromRefList("Housing_Type")}
                      disabled={!bookingsFieldsEnabled}
                      onChange={(value) =>
                        handlecellBedListOnChange(value, "blockList")
                      }
                    />
                  </FormItem>
                </StyledGridCol>
                {inJailType === "Cell" && (
                  <>
                    <StyledGridCol span={5}>
                      <FormItem
                        name="block"
                        label="block"
                        rules={[
                          { required: true, message: "Block is required" },
                        ]}
                      >
                        <Select
                          showSearch={false}
                          variant="outlined"
                          disabled={!bookingsFieldsEnabled}
                          options={
                            Array.isArray(blockOptions)
                              ? blockOptions
                              : getOptionsFromRefList("Block")
                          }
                          onChange={(value) =>
                            handlecellBedListOnChange(value, "cellList")
                          }
                        />
                      </FormItem>
                    </StyledGridCol>
                    <StyledGridCol span={5}>
                      <FormItem name="cell" label="Cell">
                        <Select
                          showSearch={false}
                          variant="outlined"
                          options={
                            Array.isArray(cellOptions)
                              ? cellOptions
                              : getOptionsFromRefList("Cell")
                          }
                          disabled={!bookingsFieldsEnabled}
                          onChange={(value) =>
                            handlecellBedListOnChange(value, "bedList")
                          }
                        />
                      </FormItem>
                    </StyledGridCol>
                    <StyledGridCol span={5}>
                      <FormItem name="bed" label={labels.bed}>
                        <Select
                          showSearch={false}
                          variant="outlined"
                          options={
                            Array.isArray(bedOptions)
                              ? bedOptions
                              : getOptionsFromRefList("Bed")
                          }
                          disabled={!bookingsFieldsEnabled}
                        />
                      </FormItem>
                    </StyledGridCol>
                  </>
                )}

                {inJailType === "Holding" && (
                  <StyledGridRow gutter={16}>
                    <StyledGridCol span={24}>
                      <FormItem
                        name="holdingCell"
                        label={labels.holdingIslolationCell}
                      >
                        <Select
                          placeholder="Holding/Isolation Cell"
                          showSearch={false}
                          variant="outlined"
                          disabled={!bookingsFieldsEnabled}
                          options={
                            Array.isArray(blockOptions)
                              ? blockOptions
                              : getOptionsFromRefList("Block")
                          }
                        />
                      </FormItem>
                    </StyledGridCol>
                  </StyledGridRow>
                )}
              </StyledGridRow>
            )}

            {inmateLocation === "Loan" && (
              <StyledGridRow gutter={[16]}>
                <StyledGridCol span={7}>
                  <FormItem
                    name="loanType"
                    label={labels.loanType}
                    rules={[
                      { required: true, message: "Loan Type is required" },
                    ]}
                  >
                    <Select
                      placeholder="Loan Type"
                      showSearch={false}
                      variant="outlined"
                      options={getOptionsFromRefList("Loan_Type")}
                      disabled={!bookingsFieldsEnabled}
                    />
                  </FormItem>
                </StyledGridCol>

                {loanType !== "Court" && loanType !== "Program" && (
                  <StyledGridCol span={8}>
                    <FormItem
                      name="loanDestination"
                      label={labels.destination}
                      rules={[
                        {
                          required: true,
                          message: "Loan destination is required",
                        },
                      ]}
                    >
                      <Select
                        showSearch={false}
                        placeholder="Destination"
                        variant="outlined"
                        options={getOptionsFromRefList("Loan_Destination")}
                        disabled={!bookingsFieldsEnabled}
                      />
                    </FormItem>
                  </StyledGridCol>
                )}

                {loanType === "Court" && (
                  <StyledGridCol span={8}>
                    <FormItem
                      name="court"
                      label="Court"
                      rules={[
                        {
                          required: true,
                          message: "Court is required",
                        },
                      ]}
                    >
                      <Select
                        showSearch={false}
                        placeholder="Court"
                        variant="outlined"
                        options={getOptionsFromRefList("Court")}
                        disabled={!bookingsFieldsEnabled}
                      />
                    </FormItem>
                  </StyledGridCol>
                )}

                {loanType === "Program" && (
                  <StyledGridCol span={8}>
                    <FormItem
                      name="programName"
                      label="Program"
                      rules={[
                        {
                          required: true,
                          message: "Program is required",
                        },
                      ]}
                    >
                      <Select
                        showSearch={false}
                        placeholder="Program"
                        variant="outlined"
                        options={getOptionsFromRefList("Program_Name")}
                        disabled={!bookingsFieldsEnabled}
                      />
                    </FormItem>
                  </StyledGridCol>
                )}

                <StyledGridCol span={9}>
                  <FormItem
                    name="expectedBackDateTime"
                    label={labels.expectedDateTime}
                    dependencies={["scheduleDateTime"]}
                    rules={[
                      {
                        required: true,
                        message: "Expected back date & time is required",
                      },
                      ({ getFieldValue }) => ({
                        // Only checked when a Scheduled Date/Time is present
                        // (i.e. Status = Pending) - mirrors ASP's
                        // ValidateMovementDetailValues.
                        validator(_, value) {
                          const scheduled = getFieldValue("scheduleDateTime");
                          if (!value || !scheduled) return Promise.resolve();
                          if (dayjs(value).isBefore(dayjs(scheduled))) {
                            return Promise.reject(
                              new Error(
                                "Expected back date should not be before the scheduled date.",
                              ),
                            );
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <CommonDatePicker
                      placeholder="MM/DD/YYYY HH:mm"
                      format="MM/DD/YYYY HH:mm"
                      showTime={true}
                      style={{
                        border: "2px solid rgba(82, 140, 181, 0.27)",
                      }}
                    />
                  </FormItem>
                </StyledGridCol>
              </StyledGridRow>
            )}

            {inmateLocation === "Perm Housing Change" && (
              <StyledGridRow gutter={[16]}>
                <StyledGridCol span={7}>
                  <FormItem
                    name="destination"
                    label={labels.destination}
                    rules={[
                      {
                        required: true,
                        message: "Transport destination is required",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Destination"
                      variant="outlined"
                      showSearch={false}
                      options={getOptionsFromRefList("Loan_Destination")}
                    />
                  </FormItem>
                </StyledGridCol>

                <StyledGridCol>
                  <FormItem name="transDoc" label={labels.transportDocument}>
                    <FileUpload
                      payload={{ documentType: "transport document" }}
                    ></FileUpload>
                  </FormItem>
                </StyledGridCol>
              </StyledGridRow>
            )}

            {inmateLocation == "Transport" && (
              <StyledGridRow gutter={[16]}>
                <StyledGridCol span={7}>
                  <FormItem
                    name="transportDestination"
                    label={labels.destination}
                    rules={[
                      {
                        required: true,
                        message: "Transport destination is required",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Destination"
                      variant="outlined"
                      showSearch={false}
                      options={getOptionsFromRefList("Loan_Destination")}
                    />
                  </FormItem>
                </StyledGridCol>
                <StyledGridCol span={5}>
                  {/* <FormItem name="transDoc" label={labels.transportDocument}>
                  <FileUpload documentType="transport document"></FileUpload>
                </FormItem> */}
                  <FormItem name="transDoc" label={""} paddingTop="30px">
                    <Button
                      type="secondary"
                      color="secondary"
                      onClick={handleTransportDocument}
                      disabled
                    >
                      Transport Document
                    </Button>
                  </FormItem>
                </StyledGridCol>
                <StyledGridCol span={8}>
                  <FormItem
                    name="expectedBackDateTime"
                    label={labels.expectedDateTime}
                  >
                    <CommonDatePicker
                      style={{
                        border: "2px solid rgba(82, 140, 181, 0.27)",
                      }}
                      placeholder="MM/DD/YYYY HH:mm"
                      format="MM/DD/YYYY HH:mm"
                    />
                  </FormItem>
                </StyledGridCol>
              </StyledGridRow>
            )}

            <StyledGridRow gutter={[16]}>
              <StyledGridCol span={5}>
                <FormItem name="moveReason" label={labels.reasonForMove}>
                  <Select
                    placeholder="Reason for Move"
                    variant="outlined"
                    showSearch={false}
                    options={getOptionsFromRefList("Move_Reason")}
                    disabled={!bookingsFieldsEnabled}
                  />
                </FormItem>
              </StyledGridCol>

              <StyledGridCol span={6}>
                <FormItem
                  name="approvingOfficerStaffID"
                  label={labels?.approvalOfficer}
                >
                  <Select
                    placeholder={labels?.approvalOfficer}
                    readOnly={mode === INMATE_DETAILS_MODE.VIEW}
                    showSearch={false}
                    options={getOptionsFromRefList("", true, true, true)}
                    disabled={!bookingsFieldsEnabled}
                  />
                </FormItem>
              </StyledGridCol>

              <StyledGridCol span={6}>
                <FormItem
                  name="transportingOfficerStaffID"
                  label={labels?.transportingOfficer}
                >
                  <Select
                    placeholder={labels?.transportingOfficer}
                    readOnly={mode === INMATE_DETAILS_MODE.VIEW}
                    showSearch={false}
                    options={getOptionsFromRefList("", true, true)}
                    disabled={!bookingsFieldsEnabled}
                  />
                </FormItem>
              </StyledGridCol>
            </StyledGridRow>
            <StyledGridRow>
              <StyledGridCol span={16}>
                <FormItem name="comments" label={labels?.comments}>
                  <TextField
                    inputType="textarea"
                    placeholder="Comments"
                    variant="outlined"
                  />
                </FormItem>
              </StyledGridCol>
            </StyledGridRow>
            <StyledGridRow>
              <StyledGridCol>
                <FormItem
                  name="enterBusiness"
                  valuePropName="checked"
                  label="Business info?"
                  layout="horizontal"
                  className="form-item-checkbox-left"
                  style={{ marginBottom: "0" }}
                >
                  <Checkbox />
                </FormItem>
              </StyledGridCol>
            </StyledGridRow>
            {enterBusiness && (
              <BusinessInformation
                bookingsFieldsEnabled={bookingsFieldsEnabled}
              />
            )}
          </Form>
        </FormContainer>

        {isTransportModal && (
          <TransportDocumentModal
            show={isTransportModal}
            onHide={() => setIsTransportModal(false)}
          />
        )}
      </>
    );
  },
);

export default HouseTrackInmate;
